import { Notification as NotificationSchema, type Notification } from '@cockpit/shared';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '../api';
import { useStreamStore } from './stream';

/** Taille du journal gardé en mémoire (comme `GET /api/notifications`). */
const KEEP = 50;

/**
 * Lot AJ : notifications du membre connecté, partagées par la cloche de l'en-tête et la page Notifications.
 * Chargées au démarrage, puis tenues à jour en direct par l'événement SSE `notification`.
 */
export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<Notification[]>([]);
  const unread = ref(0);
  const loaded = ref(false);
  let off: (() => void) | null = null;

  async function load(): Promise<void> {
    const list = await api.notifications();
    items.value = list.items;
    unread.value = list.unreadCount;
    loaded.value = true;
  }

  function receive(notification: Notification): void {
    if (items.value.some((n) => n.id === notification.id)) return;
    items.value = [notification, ...items.value].slice(0, KEEP);
    if (!notification.readAt) unread.value += 1;
  }

  /** Écoute le flux et charge le journal (une seule écoute, quel que soit le nombre d'appels). */
  function start(): void {
    if (!off) off = useStreamStore().on('notification', NotificationSchema, receive);
    void load().catch(() => {
      // La cloche est un plus : en cas d'échec, elle reste vide jusqu'au prochain chargement.
    });
  }

  function stop(): void {
    off?.();
    off = null;
    items.value = [];
    unread.value = 0;
    loaded.value = false;
  }

  /** Marque une notification lue (aussitôt à l'écran, puis le compte renvoyé par le serveur fait foi). */
  async function markRead(id: string): Promise<void> {
    const target = items.value.find((n) => n.id === id);
    if (!target || target.readAt) return;
    const now = new Date().toISOString();
    items.value = items.value.map((n) => (n.id === id ? { ...n, readAt: now } : n));
    unread.value = Math.max(0, unread.value - 1);
    unread.value = (await api.markNotificationsRead([id])).unreadCount;
  }

  async function markAllRead(): Promise<void> {
    unread.value = (await api.markNotificationsRead()).unreadCount;
    const now = new Date().toISOString();
    items.value = items.value.map((n) => (n.readAt ? n : { ...n, readAt: now }));
  }

  return { items, unread, loaded, load, start, stop, receive, markRead, markAllRead };
});
